import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Check } from "lucide-react";

import { Close } from "@/components/common/svgs";
import { TranslatedText } from "@/components/common/text";
import { CreateFilterModal } from "@/components/dataPages/loadFinder/filter/CreateFilterModal";
import { DeleteFilterConfirmModal } from "@/components/dataPages/loadFinder/filter/DeleteFilterConfirmModal";
import { TruckCheckStep, isTruckStepBlocked } from "@/components/dataPages/loadFinder/dailyCheckIn/TruckCheckStep";
import { useLoadFinderStore } from "@/state/loadFinderStore";
import { useTruckListConfig } from "@/hooks/useTruckList";
import { trailerTypes, TruckStatus } from "@/utils/constants";

const infoToast = (msg) => toast.info(msg, { className: "cm-toast-info", toasterId: "top-right" });
const successToast = (msg) => toast.success(msg, { className: "cm-toast-success", toasterId: "top-right" });
const errorToast = (msg) => toast.error(msg, { className: "cm-toast-error", toasterId: "top-right" });

const PHASE_MY = "MY";
const PHASE_INTERSTITIAL = "INTERSTITIAL";
const PHASE_OTHER = "OTHER";

const stepKey = (t) => t.truckId ?? t.truckName;

const seedFromTruck = (t) => {
    // LocationDropdown expects { zip, placeName, stateCode }; the truck's location is { zip, city, state }.
    const location = t.location ? { zip: t.location.zip, placeName: t.location.city, stateCode: t.location.state } : null;
    return {
        status: t.status ?? TruckStatus.FREE.id,
        trailerType: t.trailerType ?? trailerTypes[0].id,
        location,
        locationQuery: t.location ? `${t.location.city}, ${t.location.state}` : "",
        deliveryDate: t.deliveryDate ? String(t.deliveryDate).slice(0, 10) : "",
        // Off-duty saves force the flag off, so it isn't a real preference; start from the default (on).
        onTruckList: t.status === TruckStatus.OFF_DUTY.id || !!t.onTruckList,
        dirty: false,
    };
};

const ProgressSegment = ({ label, reviewed, count }) => (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-center justify-between">
            <span className="text-[14px] text-cm-p-text-secondary">
                <TranslatedText>{label}</TranslatedText>
            </span>
            <span className="text-[14px] tracking-[1.4px] text-cm-p-text-secondary">
                {reviewed}/{count}
            </span>
        </div>
        <div className="h-[8px] w-full overflow-hidden rounded-[20px] bg-cm-p-surface-container">
            <div
                className="h-full rounded-[20px] bg-cm-p-accent-600 transition-all"
                style={{ width: `${count > 0 ? (reviewed / count) * 100 : 0}%` }}
            />
        </div>
    </div>
);

export const TrucksCheckModal = ({ isOpen, myTrucks = [], otherTrucks = [], truckFilters = [], onClose }) => {
    const {
        updateCompanyTruck,
        fetchTrucks,
        fetchCompanyTrucks,
        fetchTruckFilters,
        updateTruckFilter,
        updateTruckFilterState,
        deleteTruckFilter,
        deleteTruckFilterState,
    } = useLoadFinderStore();
    const { data: truckListConfig } = useTruckListConfig();
    const truckListEnabled = !!truckListConfig?.enabled;

    const M = myTrucks.length;
    const O = otherTrucks.length;
    const total = M + O;
    const isSingle = total === 1;

    const [phase, setPhase] = useState(M > 0 ? PHASE_MY : PHASE_OTHER);
    const [index, setIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [edits, setEdits] = useState({});
    const [editingFilter, setEditingFilter] = useState(null);
    const [filterToDelete, setFilterToDelete] = useState(null);

    // Reset to the start and drop stale per-truck edits whenever the modal opens, so a reopen
    // (e.g. via "Run check") reseeds from current truck data.
    useEffect(() => {
        if (isOpen) {
            setPhase(M > 0 ? PHASE_MY : PHASE_OTHER);
            setIndex(0);
            setEdits({});
        }
    }, [isOpen, M]);

    const activeTrucks = phase === PHASE_OTHER ? otherTrucks : myTrucks;
    const currentTruck = phase === PHASE_INTERSTITIAL ? null : (activeTrucks[index] ?? null);
    const currentEdit = currentTruck ? (edits[stepKey(currentTruck)] ?? seedFromTruck(currentTruck)) : null;

    const patchEdit = (patch) => {
        if (!currentTruck) return;
        const key = stepKey(currentTruck);
        setEdits((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? seedFromTruck(currentTruck)), ...patch, dirty: true },
        }));
    };

    const { expiredFilters, activeFiltersCount } = useMemo(() => {
        const mine = (truckFilters ?? []).filter((p) => p.truckId === currentTruck?.truckId);
        const today = dayjs();
        const expired = [];
        let active = 0;
        mine.forEach((p) => {
            const isExpired = p.availableDateEnd && dayjs(p.availableDateEnd.slice(0, 10)).isBefore(today, "day");
            if (isExpired) expired.push(p);
            else active += 1;
        });
        return { expiredFilters: expired, activeFiltersCount: active };
    }, [truckFilters, currentTruck]);

    const handleSubmitEditedFilter = (payload) => {
        if (!editingFilter) return;
        updateTruckFilter(editingFilter.id, payload)
            .then(() => {
                setEditingFilter(null);
                return fetchTruckFilters().catch(() => errorToast("Failed to fetch filters after update"));
            })
            .catch(() => errorToast("Failed to update filter"));
    };

    const handleConfirmDeleteFilter = () => {
        if (!filterToDelete) return;
        deleteTruckFilter(filterToDelete.id)
            .then(() => {
                setFilterToDelete(null);
                return fetchTruckFilters().catch(() => errorToast("Failed to fetch filters after delete"));
            })
            .catch(() => {});
    };

    if (!isOpen) return null;

    const isStepBlocked = currentTruck ? isTruckStepBlocked(currentEdit) : false;
    const showProgress = total > 1;
    const myReviewed = phase === PHASE_MY ? index : M;
    const otherReviewed = phase === PHASE_OTHER ? index : 0;

    const persistCurrentTruck = async () => {
        if (!currentTruck || !currentEdit?.dirty) return;
        const isOffDuty = currentEdit.status === TruckStatus.OFF_DUTY.id;
        const locationZip = currentEdit.location?.zip ?? currentTruck.location?.zip ?? null;
        const base = {
            truckName: currentTruck.truckName,
            driverName: currentTruck.driverName,
            trailerType: currentEdit.trailerType,
            status: currentEdit.status,
            deliveryDate: currentEdit.deliveryDate || null,
            // My-trucks phase keeps trucks pinned; the company phase keeps them unpinned.
            markAsMyTruck: phase === PHASE_MY,
        };
        const payload = isOffDuty
            ? { ...base, location: locationZip, onTruckList: false, note: null }
            : {
                  ...base,
                  location: locationZip,
                  note: currentTruck.note ?? null,
                  ...(truckListEnabled && { onTruckList: currentEdit.onTruckList }),
              };
        await updateCompanyTruck(currentTruck.truckId, payload);
        await fetchTrucks();
        await fetchCompanyTrucks();
    };

    // Emit per-section summaries for how many trucks were reviewed in each section, then close.
    const emitSummaries = (myPassed, otherPassed) => {
        const bothComplete = M > 0 && O > 0 && myPassed === M && otherPassed === O;
        if (bothComplete) {
            successToast("All of your trucks are up to date now!");
            return;
        }
        if (M > 0) {
            if (myPassed === 0) infoToast("Trucks remained unchanged");
            else if (myPassed < M)
                infoToast(`${myPassed} of your trucks is/are up to date. ${M - myPassed} of your trucks remained unchecked.`);
            else successToast("All of your trucks are up to date now!");
        }
        // Company summary is only relevant once the My section is fully handled (or there is none).
        if (O > 0 && myPassed === M) {
            if (otherPassed === O) successToast("All of your company trucks are up to date now!");
            else if (otherPassed === 0) infoToast("None of your company trucks are up to date.");
            else
                infoToast(
                    `${otherPassed} of your company trucks is/are up to date. ${O - otherPassed} of your company trucks remained unchecked.`,
                );
        }
    };

    const closeWith = async (myPassed, otherPassed) => {
        try {
            await onClose?.();
        } catch {
            return; // onClose reported failure; keep the modal open, no summaries.
        }
        emitSummaries(myPassed, otherPassed);
    };

    // Trucks passed (reviewed) at the current position — used when closing via X.
    const passedNow = () => {
        if (phase === PHASE_OTHER) return { my: M, other: index };
        if (phase === PHASE_INTERSTITIAL) return { my: M, other: 0 };
        return { my: index, other: 0 };
    };

    const handleClose = async () => {
        setSubmitting(true);
        try {
            const { my, other } = passedNow();
            await closeWith(my, other);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrimary = async () => {
        if (isStepBlocked) return;
        setSubmitting(true);
        try {
            await persistCurrentTruck();
            if (phase === PHASE_MY) {
                if (isSingle) {
                    await closeWith(1, 0); // lone My truck → Confirm closes
                } else if (index < M - 1) {
                    setIndex((i) => i + 1);
                } else {
                    setPhase(PHASE_INTERSTITIAL); // finished My trucks → completion screen
                }
            } else if (phase === PHASE_OTHER) {
                if (index < O - 1) setIndex((i) => i + 1);
                else await closeWith(M, O); // last company truck → Confirm closes
            }
        } catch {
            errorToast("Failed to save truck");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrevious = () => {
        if (phase === PHASE_INTERSTITIAL) {
            setPhase(PHASE_MY);
            setIndex(M - 1);
            return;
        }
        if (phase === PHASE_OTHER && index === 0) {
            if (M > 0) setPhase(PHASE_INTERSTITIAL); // back to the completion screen
            return;
        }
        setIndex((i) => Math.max(i - 1, 0));
    };

    const handleReviewOther = () => {
        setPhase(PHASE_OTHER);
        setIndex(0);
    };

    const handleFinish = async () => {
        setSubmitting(true);
        try {
            await closeWith(M, 0);
        } finally {
            setSubmitting(false);
        }
    };

    // Primary button label: Confirm on the last actionable step, else Next.
    const primaryIsConfirm = (phase === PHASE_MY && isSingle) || (phase === PHASE_OTHER && index >= O - 1);
    // Previous is hidden for the lone-truck case; disabled at the very first step of the first
    // section (first My truck, or first company truck when there are no My trucks).
    const previousDisabled = submitting || (phase === PHASE_MY && index === 0) || (phase === PHASE_OTHER && index === 0 && M === 0);
    const showPrevious = !isSingle && total > 0;

    return (
        <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/40">
            <div className="flex max-h-[calc(100vh-32px)] w-[540px] max-w-[92vw] flex-col rounded-[12px] border border-cm-p-outline bg-cm-p-surface shadow-xl">
                {/* Header */}
                <div className="flex w-full items-start justify-between border-b border-cm-p-outline px-4 py-4">
                    <div className="flex min-w-0 flex-1 flex-col">
                        <h2 className="text-[18px] font-medium tracking-[-0.18px] text-cm-p-text-primary">
                            <TranslatedText>Trucks check</TranslatedText>
                        </h2>
                        <p className="text-[12px] font-normal text-cm-p-text-secondary">
                            <TranslatedText>Verify if trucks and filters are up to date. Changes are saved as you go.</TranslatedText>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        aria-label="Close"
                        className="ml-3 shrink-0 rounded p-1 hover:bg-cm-p-surface-hover disabled:opacity-50"
                    >
                        <Close />
                    </button>
                </div>

                {/* Body */}
                <div className="flex min-h-0 w-full flex-1 flex-col gap-5 px-4 pb-6 pt-3" data-testid="trucks-check-step">
                    {showProgress && (
                        <div className="flex w-full items-start gap-4">
                            {M > 0 && <ProgressSegment label="My trucks" reviewed={myReviewed} count={M} />}
                            {O > 0 && <ProgressSegment label="Other trucks" reviewed={otherReviewed} count={O} />}
                        </div>
                    )}

                    {phase === PHASE_INTERSTITIAL ? (
                        <div className="flex w-full flex-col items-center gap-3 py-6 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cm-p-accent-50">
                                <Check className="h-6 w-6 text-cm-p-on-container-green" />
                            </div>
                            <p className="text-[18px] font-medium text-cm-p-text-primary">
                                {M === 1 ? (
                                    <TranslatedText>You've reviewed your truck!</TranslatedText>
                                ) : (
                                    <>
                                        <TranslatedText>You've reviewed all of your</TranslatedText> {M}{" "}
                                        <TranslatedText>trucks!</TranslatedText>
                                    </>
                                )}
                            </p>
                            <p className="text-[14px] text-cm-p-text-secondary">
                                {O > 0 ? (
                                    <TranslatedText>You can also review the company trucks, or finish here.</TranslatedText>
                                ) : (
                                    <TranslatedText>You're all set.</TranslatedText>
                                )}
                            </p>
                        </div>
                    ) : currentTruck ? (
                        <TruckCheckStep
                            truck={currentTruck}
                            edit={currentEdit}
                            onPatch={patchEdit}
                            expiredFilters={expiredFilters}
                            activeFiltersCount={activeFiltersCount}
                            onEditFilter={setEditingFilter}
                            onDeleteFilter={setFilterToDelete}
                            truckListEnabled={truckListEnabled}
                        />
                    ) : (
                        <div className="text-[14px] text-cm-p-text-secondary">
                            <TranslatedText>You have no trucks to review.</TranslatedText>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex w-full items-center justify-end gap-3 border-t border-cm-p-outline px-4 py-4">
                    {phase === PHASE_INTERSTITIAL ? (
                        <>
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={submitting}
                                className="flex h-[38px] w-[120px] items-center justify-center rounded-[6px] bg-cm-p-surface-container px-4 py-2 text-[14px] font-medium text-cm-p-text-primary disabled:opacity-50"
                            >
                                <TranslatedText>Previous</TranslatedText>
                            </button>
                            {O > 0 && (
                                <button
                                    type="button"
                                    onClick={handleReviewOther}
                                    disabled={submitting}
                                    className="flex h-[38px] items-center justify-center rounded-[6px] bg-cm-p-surface-container px-4 py-2 text-[14px] font-medium text-cm-p-text-primary disabled:opacity-50"
                                >
                                    <TranslatedText>Review other trucks</TranslatedText>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleFinish}
                                disabled={submitting}
                                className="flex h-[38px] w-[120px] items-center justify-center rounded-[6px] bg-cm-p-accent-500 px-4 py-2 text-[14px] font-medium text-cm-p-neutral-dark-500 disabled:opacity-50"
                            >
                                <TranslatedText>Finish</TranslatedText>
                            </button>
                        </>
                    ) : (
                        <>
                            {showPrevious && (
                                <button
                                    type="button"
                                    onClick={handlePrevious}
                                    disabled={previousDisabled}
                                    className="flex h-[38px] w-[120px] items-center justify-center rounded-[6px] bg-cm-p-surface-container px-4 py-2 text-[14px] font-medium text-cm-p-text-primary disabled:opacity-50"
                                >
                                    <TranslatedText>Previous</TranslatedText>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handlePrimary}
                                disabled={submitting || isStepBlocked || total === 0}
                                className="flex h-[38px] w-[120px] items-center justify-center rounded-[6px] bg-cm-p-accent-500 px-4 py-2 text-[14px] font-medium text-cm-p-neutral-dark-500 disabled:opacity-50"
                            >
                                {primaryIsConfirm ? <TranslatedText>Confirm</TranslatedText> : <TranslatedText>Next</TranslatedText>}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {editingFilter && (
                <CreateFilterModal
                    isOpen
                    mode="edit"
                    selectedTruck={currentTruck}
                    selectedFilter={editingFilter}
                    onClose={() => setEditingFilter(null)}
                    onSubmit={handleSubmitEditedFilter}
                    loading={updateTruckFilterState?.loading}
                    error={updateTruckFilterState?.error}
                />
            )}

            <DeleteFilterConfirmModal
                isOpen={!!filterToDelete}
                onClose={() => setFilterToDelete(null)}
                onConfirm={handleConfirmDeleteFilter}
                filter={filterToDelete}
                loading={deleteTruckFilterState?.loading}
                error={deleteTruckFilterState?.error}
            />
        </div>
    );
};
